/**
 * DAG glow-up — small behaviour layer (DOM-level helpers).
 *
 * These are intentionally vanilla so they can be called once from a single root
 * effect (Layout) without coupling to component trees. Reduced-motion safe.
 */

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * One-time card entrance on first paint. Adds body.dag-enter briefly; the CSS
 * animates `main .bg-white` panels in, then the trigger is removed so later
 * route swaps don't replay it.
 */
export function initEntrance(): (() => void) | void {
  if (prefersReducedMotion()) return;
  document.body.classList.add("dag-enter");
  const id = window.setTimeout(() => document.body.classList.remove("dag-enter"), 900);
  return () => {
    window.clearTimeout(id);
    document.body.classList.remove("dag-enter");
  };
}

export type ToastKind = "info" | "success" | "error";

/**
 * Gold-accented toast, bottom-right. Returns immediately; the toast
 * auto-dismisses. Safe to call when the flag is off (no-op).
 */
export function dagToast(
  message: string,
  kind: ToastKind = "info",
  durationMs = 4200
): void {
  if (typeof document === "undefined") return;

  let wrap = document.querySelector<HTMLDivElement>(".dag-toast-wrap");
  if (!wrap) {
    wrap = document.createElement("div");
    wrap.className = "dag-toast-wrap";
    wrap.setAttribute("aria-live", "polite");
    wrap.setAttribute("role", "status");
    document.body.appendChild(wrap);
  }

  const toast = document.createElement("div");
  toast.className = `dag-toast dag-toast--${kind}`;
  toast.textContent = message;
  wrap.appendChild(toast);

  // next frame → trigger the enter transition
  requestAnimationFrame(() => toast.classList.add("is-in"));

  const remove = () => {
    toast.classList.remove("is-in");
    window.setTimeout(() => toast.remove(), 300);
  };
  window.setTimeout(remove, durationMs);
  toast.addEventListener("click", remove);
}

/** Expose the toast helper globally for non-React callers (optional). */
export function registerGlobals(): void {
  if (typeof window === "undefined") return;
  (window as unknown as { dagToast?: typeof dagToast }).dagToast = dagToast;
}
